import { Car } from 'lucide-react';
import { Link } from 'react-router-dom';

import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

import type { StockSearchHit } from '@/lib/api/dealer-kit.api';

export interface CardData {
  id: string;
  title: string;
  price: number | null;
  year: number | null;
  mileage: number | null;
  fuel_type: string | null;
  transmission: string | null;
  image: string | null;
  distance_miles?: number;
  status?: string | null;
  sourceLabel?: string;
}

export const carPath = (id: string) => `/crm-integration/stock/car/${id}`;

export const hitToCardData = (hit: StockSearchHit, sourceLabel: string): CardData => ({
  id: hit.id,
  title: [hit.manufacturer, hit.model, hit.trim].filter(Boolean).join(' '),
  price: hit.price,
  year: hit.year,
  mileage: hit.mileage,
  fuel_type: hit.fuel_type,
  transmission: hit.transmission,
  image: hit.images?.[0] ?? null,
  distance_miles: hit.distance_miles,
  status: hit.status,
  sourceLabel,
});

export const StockCarCardView = ({
  car,
  compact = false,
  newTab = false,
}: {
  car: CardData;
  compact?: boolean;
  /** Open the car in a new tab (used inside the chat drawer so the conversation survives). */
  newTab?: boolean;
}) => {
  const meta = [
    car.year,
    car.mileage != null ? `${car.mileage.toLocaleString('en-GB')} mi` : null,
    car.fuel_type,
    car.transmission,
  ].filter(Boolean);
  return (
    <Link
      to={carPath(car.id)}
      {...(newTab ? { target: '_blank', rel: 'noreferrer' } : {})}
      className={cn(
        'bg-card hover:border-primary/40 focus-visible:ring-ring group flex cursor-pointer overflow-hidden rounded-lg border transition-colors duration-200 hover:shadow-md focus-visible:ring-2 focus-visible:outline-none',
        compact ? 'flex-row' : 'flex-col',
      )}
    >
      <div
        className={cn(
          'bg-muted relative shrink-0',
          compact ? 'h-20 w-28' : 'aspect-[16/10] w-full',
        )}
      >
        {car.image ? (
          <img src={car.image} alt="" loading="lazy" className="h-full w-full object-cover" />
        ) : (
          <div className="text-muted-foreground flex h-full w-full items-center justify-center">
            <Car className="h-8 w-8" aria-hidden />
          </div>
        )}
      </div>
      <div className={cn('flex min-w-0 flex-1 flex-col gap-1', compact ? 'p-2' : 'p-3')}>
        <p className={cn('font-bold', compact ? 'text-sm' : 'text-lg')}>
          {car.price != null ? `£${car.price.toLocaleString('en-GB')}` : 'Price on request'}
        </p>
        <p className="truncate text-sm font-medium" title={car.title}>
          {car.title || 'Unnamed vehicle'}
        </p>
        <p className="text-muted-foreground truncate text-xs">{meta.join(' · ')}</p>
        {!compact && (
          <div className="mt-auto flex flex-wrap gap-1 pt-1">
            {car.sourceLabel && (
              <Badge variant="outline" className="text-xs font-normal">
                {car.sourceLabel}
              </Badge>
            )}
            {car.status && (
              <Badge variant="secondary" className="text-xs font-normal capitalize">
                {car.status}
              </Badge>
            )}
            {car.distance_miles != null && (
              <Badge variant="outline" className="text-xs font-normal">
                {car.distance_miles} mi away
              </Badge>
            )}
          </div>
        )}
      </div>
    </Link>
  );
};
